import { withZod } from '@remix-validated-form/with-zod'
import { useState } from 'react'
import { z } from 'zod'
import Button from '../Button'
import ComboMultiSelectAsync from 'app/components/ui/combo/combo-multi-select-async'
import { fetchBlockoutNotes } from 'app/graphql/blockoutNotes'
import FormCombo from '../form/FormCombo'
import FormComboMultiSelect from '../form/FormComboMultiSelect'
import FormInput from '../form/FormInput'
import { SmartForm } from '../form/SmartForm'
import type { AIAgentConfigurationType } from 'app/types/entities'
import { AppointmentTypeType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { getCategoriesForAppointmentType } from 'app/table-columns/appointment-types'
import { OptionType } from 'types/OptionType'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'

const editSchema = z.object({
  id: z.string().min(1, { message: `An appointment type id is required` }),
  alias: z.string().optional(),
  patientType: z.string().optional(),
  blockScheduling: z
    .string()
    .transform((val) => (val === `enabled` ? true : false))
    .optional(),
  appointmentLength: z.preprocess((val) => (val ? Number(val) : undefined), z.number().min(0).optional()),
  startIncrement: z.preprocess(
    (val) => (val === `` || val == null ? undefined : Number(val)),
    z.number().min(0).optional(),
  ),
  order: z.preprocess(
    (val) => (val === `` || val === undefined ? undefined : Number(val)),
    z.number().min(1).optional(),
  ),
  excludedBlockouts: z
    .array(z.object({ value: z.string(), id: z.string(), name: z.string() }).transform((option) => option.value))
    .optional(),
  blockSchedulingNotes: z
    .array(z.object({ value: z.string(), id: z.string(), name: z.string() }).transform((option) => option.value))
    .optional(),
  doubleBookingExclusion: z
    .string()
    .transform((val) => val === `true`)
    .optional(),
  activeScheduler: z
    .string()
    .transform((val) => val === `true`)
    .optional(),
  activeAgent: z
    .string()
    .transform((val) => val === `true`)
    .optional(),
  generalBuffer: z.preprocess((val) => (val === `` || val == null ? 0 : Number(val)), z.number().min(0).optional()),
  morningBuffer: z.preprocess((val) => (val === `` || val == null ? 0 : Number(val)), z.number().min(0).optional()),
  morningBufferLookahead: z.preprocess(
    (val) => (val === `` || val == null ? 0 : Number(val)),
    z.number().min(0).optional(),
  ),
})

export const editValidator = withZod(editSchema)

const patientTypeOptions: OptionType[] = [
  {
    id: `new`,
    name: `New Patient`,
    value: `New Patient`,
  },
  {
    id: `existing`,
    name: `Existing Patient`,
    value: `Existing Patient`,
  },
  {
    id: `any`,
    name: `Any`,
    value: undefined,
  },
]

const blockSchedulingOptions: OptionType[] = [
  {
    id: `enabled`,
    name: `Enabled`,
    value: `enabled`,
  },
  {
    id: `disabled`,
    name: `Disabled`,
    value: `disabled`,
  },
]

const doubleBookingExclusionOptions: OptionType[] = [
  { id: `false`, name: `No`, value: `false` },
  { id: `true`, name: `Yes`, value: `true` },
]

const enabledProductsOptions: OptionType[] = [
  { id: `scheduler`, name: `Online Scheduler`, value: `scheduler` },
  { id: `agent`, name: `AI Agent`, value: `agent` },
]

const appointmentCategoryOptions = [
  { name: `General`, id: `general`, value: `general` },
  { name: `Emergency`, id: `emergency`, value: `emergency` },
  { name: `Consultation`, id: `consultation`, value: `consultation` },
  { name: `Limited Exam`, id: `limited exam`, value: `limited exam` },
]

export function addAutoEnabledProducts(selectedProducts: OptionType[]): OptionType[] {
  const selectedValues = new Set(selectedProducts.map((option) => option.value))
  return [...selectedProducts, ...enabledProductsOptions.filter((option) => !selectedValues.has(option.value))]
}

export type EditAppointmentTypeSaveInput = {
  alias?: string
  patientType?: string
  order?: number
  appointmentLength?: number
  startIncrement?: number
  blockScheduling?: boolean
  activeScheduler?: boolean
  activeAgent?: boolean
  doubleBookingExclusion?: boolean
  excludedBlockouts?: string[]
  blockSchedulingNotes?: string[]
  generalBuffer?: number
  morningBuffer?: number
  morningBufferLookahead?: number
}

export function EditAppointmentTypeModal({
  onAppointmentTypeUpdated,
  appointmentType,
  excludableAppointmentTypes,
  allowDoubleBooking,
  isUnified,
  isCarestack,
  agentConfig,
  onAppointmentCategoryChange,
  onSave,
}: {
  onAppointmentTypeUpdated: (appointmentType: AppointmentTypeType) => void
  appointmentType: AppointmentTypeType
  excludableAppointmentTypes: { id: string; name: string }[]
  allowDoubleBooking?: boolean
  isUnified?: boolean
  agentConfig?: AIAgentConfigurationType
  isCarestack?: boolean
  onAppointmentCategoryChange?: (appointmentTypeId: string, categories: string[]) => void
  onSave: (id: string, input: EditAppointmentTypeSaveInput) => Promise<AppointmentTypeType | undefined>
}) {
  const [selectedPatientType, setSelectedPatientType] = useState<OptionType | undefined>(
    patientTypeOptions.find((option) => option.value === appointmentType.patientType),
  )

  const [selectedBlockScheduling, setSelectedBlockScheduling] = useState<OptionType | undefined>(
    blockSchedulingOptions.find((option) =>
      appointmentType.blockScheduling === true ? option.value === `enabled` : option.value === `disabled`,
    ),
  )

  const excludableOptions: OptionType[] = excludableAppointmentTypes.map((apt) => ({
    id: apt.id,
    name: apt.name,
    value: apt.id,
  }))

  const [selectedExcludedBlockouts, setSelectedExcludedBlockouts] = useState<OptionType[]>(
    (appointmentType.excludedBlockouts || [])
      .map((ref) => {
        if (ref.startsWith(`note:`)) {
          const noteName = ref.slice(5)
          return { id: ref, name: noteName, value: ref }
        }
        return excludableOptions.find((opt) => opt.id === ref)
      })
      .filter((opt): opt is OptionType => !!opt),
  )

  const [selectedBlockSchedulingNotes, setSelectedBlockSchedulingNotes] = useState<OptionType[]>(
    (appointmentType.blockSchedulingNotes || []).map((ref) => {
      const noteName = ref.startsWith(`note:`) ? ref.slice(5) : ref
      const noteValue = ref.startsWith(`note:`) ? ref : `note:${ref}`
      return { id: noteValue, name: noteName, value: noteValue }
    }),
  )

  const [selectedDoubleBookingExclusion, setSelectedDoubleBookingExclusion] = useState<OptionType | undefined>(
    doubleBookingExclusionOptions.find((option) =>
      appointmentType.doubleBookingExclusion ? option.value === `true` : option.value === `false`,
    ),
  )

  const [selectedEnabledProducts, setSelectedEnabledProducts] = useState<OptionType[]>(() => {
    // If categories are already mapped, auto-enable both products
    const hasMappedCategories =
      isUnified &&
      agentConfig &&
      appointmentType.id &&
      getCategoriesForAppointmentType(agentConfig, appointmentType.id).length > 0
    if (hasMappedCategories) return [...enabledProductsOptions]

    const selected: OptionType[] = []
    if (appointmentType?.activeScheduler) selected.push(enabledProductsOptions[0])
    if (appointmentType?.activeAgent) selected.push(enabledProductsOptions[1])
    return selected
  })

  const [selectedCategories, setSelectedCategories] = useState<OptionType[]>(() => {
    if (!isUnified || !agentConfig || !appointmentType.id) return []
    const mapped = getCategoriesForAppointmentType(agentConfig, appointmentType.id)
    return appointmentCategoryOptions.filter((o) => mapped.includes(o.id))
  })

  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async (data: Record<string, unknown>) => {
    setIsSaving(true)
    const input: EditAppointmentTypeSaveInput = {
      alias: (data.alias as string) || undefined,
      patientType: selectedPatientType?.value ?? undefined,
      order: data.order != null ? Number(data.order) : undefined,
      appointmentLength: data.appointmentLength != null ? Number(data.appointmentLength) : undefined,
      startIncrement:
        data.startIncrement != null && data.startIncrement !== `` ? Number(data.startIncrement) : undefined,
      blockScheduling: selectedBlockScheduling?.value === `enabled`,
      activeScheduler: selectedEnabledProducts.some((o) => o.value === `scheduler`),
      activeAgent: selectedEnabledProducts.some((o) => o.value === `agent`),
      doubleBookingExclusion: selectedDoubleBookingExclusion?.value === `true`,
      excludedBlockouts: selectedExcludedBlockouts.map((o) => o.value),
      blockSchedulingNotes: selectedBlockSchedulingNotes.map((o) => o.value),
      generalBuffer: data.generalBuffer != null ? Number(data.generalBuffer) : 0,
      morningBuffer: data.morningBuffer != null ? Number(data.morningBuffer) : 0,
      morningBufferLookahead: data.morningBufferLookahead != null ? Number(data.morningBufferLookahead) : 0,
    }

    const updated = await onSave(appointmentType.id!, input)
    setIsSaving(false)
    if (updated) {
      notifySuccess(`Appointment type updated`)
      if (onAppointmentCategoryChange && appointmentType.id) {
        const originalCategories = agentConfig ? getCategoriesForAppointmentType(agentConfig, appointmentType.id) : []
        const newCategories = selectedCategories.map((o) => o.value)
        if (JSON.stringify([...newCategories].sort()) !== JSON.stringify([...originalCategories].sort())) {
          onAppointmentCategoryChange(appointmentType.id, newCategories)
        }
      }
      closeModal()
      onAppointmentTypeUpdated(updated)
    } else {
      notifyError(`Failed to save appointment type`)
    }
  }

  const showDoubleBooking = isUnified || allowDoubleBooking

  return (
    <div className={`flex flex-col ${isUnified ? `w-[500px] max-w-[90vw]` : `w-96 max-w-[90vw]`}`}>
      <SmartForm
        className="!mt-0 max-w-none"
        onCancel={() => closeModal()}
        validator={editValidator}
        hiddenInputs={[{ name: `id`, value: appointmentType.id }]}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={(data) => {
          if (!data) return
          handleSave(data as Record<string, unknown>)
        }}
        hideButtons
      >
        <div className="col-span-6 w-full">
          <div className="col-span-6 grid grid-cols-6 gap-6 p-6">
            <FormInput
              name="alias"
              label="Display Name"
              placeholder={appointmentType?.displayName || (isUnified ? `Appointment name` : `Name shown on scheduler`)}
              labelClassName="text-label-sm text-strong"
              defaultValue={appointmentType?.alias}
              cols={6}
            />
            {isUnified && !isCarestack && (
              <FormInput
                name="appointmentLength"
                label="Duration (Minutes)"
                placeholder="Duration in minutes"
                labelClassName="text-label-sm text-strong"
                defaultValue={appointmentType?.appointmentLength?.toString()}
                type="number"
                cols={6}
              />
            )}
            {isUnified && !isCarestack && (
              <FormInput
                name="startIncrement"
                label="Start Time Increment (Minutes)"
                placeholder="Defaults to location time increment"
                labelClassName="text-label-sm text-strong"
                defaultValue={appointmentType?.startIncrement?.toString()}
                type="number"
                cols={6}
              />
            )}
            <FormCombo
              cols={6}
              name="patientType"
              label={isUnified ? `Patient Types` : `Patient Type`}
              placeholder={isUnified ? `Current / New` : `Who can book this appointment?`}
              options={patientTypeOptions}
              value={selectedPatientType}
              onChange={(option) => setSelectedPatientType(option)}
              labelClassName="text-label-sm text-strong"
            />
            <FormInput
              name="order"
              label="Display Order"
              placeholder="Lower numbers display first"
              labelClassName="text-label-sm text-strong"
              defaultValue={appointmentType?.order?.toString()}
              type="number"
              cols={6}
            />
            {!isCarestack && (
              <FormCombo
                cols={6}
                name="blockScheduling"
                label="Block Scheduling"
                placeholder={isUnified ? `Enabled / Disabled` : undefined}
                options={blockSchedulingOptions}
                value={selectedBlockScheduling}
                onChange={(option) => {
                  setSelectedBlockScheduling(option)
                  if (option?.value === `enabled`) {
                    setSelectedExcludedBlockouts([])
                  } else {
                    setSelectedBlockSchedulingNotes([])
                  }
                }}
                labelClassName="text-label-sm text-strong"
              />
            )}
            {!isCarestack && (
              <div className="col-span-6">
                <p className="text-label-sm text-strong">Block Scheduling Notes</p>
                <ComboMultiSelectAsync
                  name="blockSchedulingNotes"
                  fetcher={fetchBlockoutNotes}
                  additionalParams={{ locationId: appointmentType.kollaConsumerId || `` }}
                  optionsMapper={(res: string[]) =>
                    (res || []).map((name) => ({
                      id: name.startsWith(`note:`) ? name : `note:${name}`,
                      name: name.startsWith(`note:`) ? name.slice(5) : name,
                      value: name.startsWith(`note:`) ? name : `note:${name}`,
                    }))
                  }
                  values={selectedBlockSchedulingNotes.map((o) => o.value)}
                  queryValues={false}
                  onChangeHandler={(options) => setSelectedBlockSchedulingNotes(options)}
                  placeholder={
                    selectedBlockScheduling?.value !== `enabled`
                      ? `Requires Block Scheduling`
                      : `Select note types to allow`
                  }
                  disabled={selectedBlockScheduling?.value !== `enabled`}
                  className="mt-1.5"
                />
              </div>
            )}
            {!isCarestack && (
              <div className="col-span-6">
                <p className="text-label-sm text-strong">{isUnified ? `Exclude Blockouts` : `Excluded Blockouts`}</p>
                <ComboMultiSelectAsync
                  name="excludedBlockouts"
                  fetcher={fetchBlockoutNotes}
                  additionalParams={{ locationId: appointmentType.kollaConsumerId || `` }}
                  additionalChoices={excludableOptions}
                  optionsMapper={(res: string[]) =>
                    (res || []).map((name) => ({
                      id: name.startsWith(`note:`) ? name : `note:${name}`,
                      name: name.startsWith(`note:`) ? name.slice(5) : name,
                      value: name.startsWith(`note:`) ? name : `note:${name}`,
                    }))
                  }
                  values={selectedExcludedBlockouts.map((o) => o.value)}
                  queryValues={false}
                  onChangeHandler={(options) => setSelectedExcludedBlockouts(options)}
                  placeholder={
                    selectedBlockScheduling?.value === `enabled`
                      ? `Disabled with Block Scheduling`
                      : isUnified
                        ? `Select blockouts to exclude`
                        : `Select appointment types to exclude`
                  }
                  disabled={selectedBlockScheduling?.value === `enabled`}
                  className="mt-1.5"
                />
              </div>
            )}
            {!isUnified && !isCarestack && (
              <FormInput
                name="appointmentLength"
                label="Duration (minutes)"
                placeholder="Duration in minutes"
                labelClassName="text-label-sm text-strong"
                defaultValue={appointmentType?.appointmentLength?.toString()}
                type="number"
                cols={6}
              />
            )}
            {!isUnified && !isCarestack && (
              <FormInput
                name="startIncrement"
                label="Start Time Increment (minutes)"
                placeholder="Defaults to location time increment"
                labelClassName="text-label-sm text-strong"
                defaultValue={appointmentType?.startIncrement?.toString()}
                type="number"
                cols={6}
              />
            )}
            {showDoubleBooking && !isCarestack && (
              <FormCombo
                cols={6}
                name="doubleBookingExclusion"
                label="Double Booking Exclusion"
                placeholder={isUnified ? `True / False` : `Exclude from double booking?`}
                options={doubleBookingExclusionOptions}
                value={selectedDoubleBookingExclusion}
                onChange={(option) => setSelectedDoubleBookingExclusion(option)}
                labelClassName="text-label-sm text-strong"
              />
            )}
            {isUnified ? (
              <FormComboMultiSelect
                cols={6}
                name="enabledProducts"
                label="Enabled Products"
                placeholder="AI Agent / Online Scheduler"
                options={enabledProductsOptions}
                values={selectedEnabledProducts}
                onChange={(options) => setSelectedEnabledProducts(options)}
                labelClassName="text-label-sm text-strong"
              />
            ) : (
              <div className="col-span-6">
                <p className="text-label-sm text-strong">Enabled Products</p>
                <div className="mt-2 flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="activeScheduler"
                      value="true"
                      defaultChecked={appointmentType?.activeScheduler}
                      className="h-4 w-4 rounded border-strong text-default"
                    />
                    <span className="text-body-sm text-strong">Scheduler</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="activeAgent"
                      value="true"
                      defaultChecked={appointmentType?.activeAgent}
                      className="h-4 w-4 rounded border-strong text-default"
                    />
                    <span className="text-body-sm text-strong">Agent</span>
                  </label>
                </div>
              </div>
            )}
            {isUnified && agentConfig && onAppointmentCategoryChange && (
              <div className="col-span-6">
                <p className="text-label-sm text-strong">Appointment Category</p>
                <ComboMultiSelect
                  name="appointmentCategory"
                  options={appointmentCategoryOptions}
                  values={selectedCategories}
                  onChange={(vals) => {
                    setSelectedCategories(vals)
                    if (vals.length > 0) {
                      setSelectedEnabledProducts((current) => addAutoEnabledProducts(current))
                    }
                  }}
                  className="mt-1.5"
                  placeholder="Select categories"
                />
              </div>
            )}
            {isUnified && (
              <div className="col-span-6 border-t pt-4">
                <p className="text-label-sm font-medium text-strong mb-1">Availability Buffer</p>
                <p className="text-body-xs text-subtle mb-3">
                  Control how soon appointments are offered to give staff prep time.
                </p>
                <div className="grid grid-cols-6 gap-4">
                  <FormInput
                    name="generalBuffer"
                    label="General Buffer (Hours)"
                    placeholder="e.g. 24"
                    labelClassName="text-label-sm text-strong"
                    defaultValue={appointmentType?.generalBuffer?.toString() || ``}
                    type="number"
                    cols={6}
                  />
                  <FormInput
                    name="morningBuffer"
                    label="Morning Buffer (Hours)"
                    placeholder="e.g. 1"
                    labelClassName="text-label-sm text-strong"
                    defaultValue={appointmentType?.morningBuffer?.toString() || ``}
                    type="number"
                    cols={3}
                  />
                  <FormInput
                    name="morningBufferLookahead"
                    label="Morning Lookahead (Hours)"
                    placeholder="e.g. 48"
                    labelClassName="text-label-sm text-strong"
                    defaultValue={appointmentType?.morningBufferLookahead?.toString() || ``}
                    type="number"
                    cols={3}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sticky bottom-0 z-10 col-span-6 flex shrink-0 justify-end rounded-b-lg border-t bg-surface-raised p-3">
            <div className="flex justify-end gap-4">
              <Button label="Cancel" mode="white" disabled={isSaving} size="md" onClick={() => closeModal()} />
              <Button type="submit" label="Save" mode="primary" loading={isSaving} size="md" />
            </div>
          </div>
        </div>
      </SmartForm>
    </div>
  )
}
